<template>
  <div class="mini-calculator">
    <div
      v-if="queryLogo !== 'hide'"
      class="mini-calculator-full-width"
    >
      <img
        src="/svg/logo/vz-logo.svg"
        style="height: 22px; margin-bottom: 10px"
        alt=""
      />
    </div>
    <template v-if="queryFrom !== 'hide'">
      <city-autocomplete
        v-model="form.dispatch.guid"
        :disabled="queryFrom === 'disabled'"
        label="Откуда"
        required
        label-public-style
      />
      <vz-checkbox
        v-model="form.dispatch.address"
        :disabled="queryFrom === 'disabled'"
        class="mini-calculator-checkbox"
        label="От адреса"
      />
    </template>
    <template v-if="queryTo !== 'hide'">
      <city-autocomplete
        v-model="form.destination.guid"
        :disabled="queryTo === 'disabled'"
        label="Куда"
        required
        label-public-style
      />
      <vz-checkbox
        v-model="form.destination.address"
        :disabled="queryTo === 'disabled'"
        class="mini-calculator-checkbox"
        label="До адреса"
      />
    </template>
    <div
      v-if="queryDimensions !== 'hide'"
      class="mini-calculator-dimensions"
    >
      <vz-measure-input
        v-model="form.volume"
        :disabled="queryDimensions === 'disabled'"
        label="Объем"
        placeholder="0.01"
        :max-value="79.9"
        :min-value="0.01"
        :measure="m3"
        with-max-notice
      />
      <vz-measure-input
        v-model="form.weight"
        :disabled="queryDimensions === 'disabled'"
        label="Вес"
        :max-value="19999"
        :min-value="0.1"
        with-max-notice
        measure="кг"
      />
    </div>
    <div class="mini-calculator-dimensions">
      <vz-select
        v-if="queryWrapping !== 'hide'"
        :disabled="queryWrapping === 'disabled'"
        v-model="form.wrapping"
        label="Упаковка"
        :clearable="queryWrapping !== 'disabled'"
        :items="getWrappingItems"
      />
      <vz-measure-input
        v-if="queryInsurance !== 'hide'"
        :disabled="queryInsurance === 'disabled'"
        v-model="form.insurance"
        label="Страхование"
        placeholder="сумма"
        measure="₽"
      />
    </div>
    <vz-button
      class="mini-calculator-full-width flex flex-center mt-15"
      :style="`background: ${color}; border-color: ${color}`"
      large
      variant="primary"
      @click="getPrice"
    >Стоимость и сроки доставки
    </vz-button>
    <vz-loader :status-loader="loader"/>
    <div
      v-if="showResult"
      class="mini-calculator-result"
      :style="`border-color: ${color}`"
    >
      <vz-icon
        name="close"
        size="24"
        class="mini-calculator-result-close"
        @click="showResult = false"
      />
      <template v-if="priceData.error">
        <div class="color-error">
          {{ priceData.error.msg }}
        </div>
        <div>
          <span v-html="priceData.error.description"/>
        </div>
      </template>
      <template v-else>
        <div>
          <span>Стоимость:</span>
          <span>{{ priceData.price }} ₽</span>
        </div>
        <div>
          <span>Срок доставки:</span>
          <span>{{ duration }}</span>
        </div>
        <vz-button
          large
          variant="primary"
          class="w-100 flex flex-center"
          :style="`background: ${color}; border-color: ${color}`"
          :to="btnRoute"
          target="_blank"
          @click="onClick"
        >Оформить перевозку
        </vz-button>
      </template>
    </div>
    <div v-if="showResult" class="mini-calculator-result-bg" @click.self="showResult = false"/>
  </div>
</template>

<script setup lang="ts">
import CityAutocomplete from '~/components/autocomplete/city-autocomplete.vue'
import VzCheckbox from '~/components/ui-kit/vz-checkbox.vue'
import VzMeasureInput from '~/components/ui-kit/vz-measure-input.vue'
import VzSelect from '~/components/ui-kit/vz-select.vue'
import VzButton from '~/components/ui-kit/vz-button.vue'
import VzIcon from '~/components/ui-kit/vz-icon.vue'
import {getWidgetPrice} from '~/api/order'
import {MiniCalcPrice} from '~/types/types'
import {declOfNum, deepCopyObject} from '~/common/functions'
import {useWrappingStore} from '~/stores/wrapping'
import {m3} from '~/common/objects';

definePageMeta({
  layout: 'empty'
})

const {getWrappingItems} = useWrappingStore()
const route = useRoute()

const loader = ref(false)
const showResult = ref(false)
const priceData = ref<MiniCalcPrice>({
  price: 0,
  time: {
    from: 0,
    to: 0,
  }
})

const {
  color,
  logo: queryLogo,
  from: queryFrom,
  to: queryTo,
  dimensions: queryDimensions,
  insurance: queryInsurance,
  wrapping: queryWrapping
} = route.query;

const form = ref({
  dispatch: {
    guid: route.query.dispatch_point || 'e90f19de-0128-11e5-80c7-00155d903d03',
    address: route.query.dispatch_to_door === 'on' || false,
  },
  destination: {
    guid: route.query.destination_point || 'e90f1820-0128-11e5-80c7-00155d903d03',
    address: route.query.destination_to_door === 'on' || false,
  },
  volume: route.query.volume || 0.01,
  weight: route.query.weight || 0.1,
  insurance: route.query.insurance_value || 0,
  wrapping: route.query.package || ''
})

const duration = computed(() => {
  const declDays = ['день', 'дня', 'дней']
  return priceData.value.time.from === priceData.value.time.to
    ? `${priceData.value.time.from} ${declOfNum(priceData.value.time.from, declDays)}`
    : `от ${priceData.value.time.from} до ${priceData.value.time.to} ${declOfNum(priceData.value.time.to, declDays)}`
})

const btnQuery = computed(() => {
  return {
    utm_source: 'partner_site',
    utm_medium: 'iframe',
    location_dispatch: form.value.dispatch.guid,
    location_destination: form.value.destination.guid,
    dispatch_point: form.value.dispatch.address ? 'address' : 'terminal',
    destination_point: form.value.destination.address ? 'address' : 'terminal',
    insurance: form.value.insurance || undefined,
    volume: form.value.volume,
    weight: form.value.weight,
    package: form.value.wrapping
  }
})

const btnRoute = computed(() => {
  const to = {
    name: 'order-create',
    query: btnQuery.value
  }

  if (route.query.link) {
    return undefined
  }

  return to
})

async function getPrice() {
  loader.value = true
  const formData = new FormData()
  formData.append('params', JSON.stringify({
    dispatch_point: form.value.dispatch.guid,
    destination_point: form.value.destination.guid,
    dispatch_to_door: form.value.dispatch.address ? "on" : undefined,
    destination_to_door: form.value.destination.address ? "on" : undefined,
    insurance: form.value.insurance || undefined,
    volume: form.value.volume,
    weight: form.value.weight,
    package: form.value.wrapping
  }))

  priceData.value = await getWidgetPrice(formData)
  loader.value = false
  showResult.value = true
}

function onClick() {
  parent.postMessage({
    type: 'order-create',
    other: deepCopyObject(btnQuery.value)
  }, '*')
  showResult.value = false
}
</script>

<style lang="scss" scoped>
.mini-calculator {
  position: relative;
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
  padding: 20px;
  max-width: 500px;
  background-color: $white;

  &-dimensions {
    grid-column-start: 1;
    grid-column-end: 2;
    display: flex;
    gap: 10px;
  }

  &-checkbox {
    margin-top: 20px;
  }

  &-full-width {
    grid-column-start: 1;
    grid-column-end: 3;
  }

  &-result {
    position: absolute;
    top: 50px;
    left: 5%;
    right: 5%;
    z-index: $zi-modal;

    padding: 20px;
    width: 90%;
    margin: auto;
    border-top: 4px solid $primary;
    border-right: 1px solid $primary;
    border-left: 1px solid $primary;
    border-bottom: 1px solid $primary;
    background-color: white;
    font-size: $fs-18;

    > div {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
    }

    &-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background-color: white;
      opacity: 0.9;
      z-index: $zi-modal - 1;
    }

    &-close {
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  :deep(.vz-checkbox-label) {
    font-size: $fs-14;
  }

  :deep(.vz-select-dropdown) {
    display: grid;

    .vz-select-dropdown-label {
      font-size: 14px;
    }
  }

  @media screen and (max-width: 400px) {
    grid-template-columns: auto 70px;
  }
  @media screen and (max-width: 320px) {
    grid-template-columns: auto;
    padding: 10px;

    &-dimensions {
      flex-wrap: wrap;
    }

    &-full-width {
      grid-column-start: 1;
      grid-column-end: 2;
    }

    &-checkbox {
      margin-top: unset;
      margin-bottom: 20px;
    }
  }
}
</style>
